import { useState } from 'react';
import { TextField, MenuItem } from '@mui/material';
import { Acc } from '../../../../components/edit-object/design/Accordion';
import { Btn, FLabel, Input } from '../../../../components/edit-object/design/Form';
import { en } from '../../../../translations/en';
import type { ArtworkDetailsForm } from '../useArtworkDetailsForm';

const FREE_TEXT_SENTINEL = en.wizard.describe.steps.workType.editions.freeTextSentinel;

interface Props {
  form: ArtworkDetailsForm;
}

type Edition = { type: string; count: string; total: string };

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z" />
  </svg>
);

interface EditionRowsProps {
  editions: Edition[];
  setEditions: (next: Edition[]) => void;
  options: string[];
  onFirstAdd?: () => void;
}

const EditionRows = ({ editions, setEditions, options, onFirstAdd }: EditionRowsProps) => {
  const [pendingType, setPendingType] = useState('');
  const [freeTextValue, setFreeTextValue] = useState('');

  const isFreeTextMode = pendingType === FREE_TEXT_SENTINEL;

  const update = (i: number, patch: Partial<Edition>) => {
    setEditions(editions.map((e, idx) => (idx !== i ? e : { ...e, ...patch })));
  };

  const remove = (i: number) => setEditions(editions.filter((_, idx) => idx !== i));

  const usedTypes = new Set(editions.map((e) => e.type));
  const availableTypes = options.filter((o) => !usedTypes.has(o));

  const isAddDisabled = isFreeTextMode
    ? freeTextValue.trim() === '' || editions.some((e) => e.type === freeTextValue.trim())
    : !pendingType;

  const addPending = () => {
    const typeToAdd = isFreeTextMode ? freeTextValue.trim() : pendingType;
    if (!typeToAdd || editions.some((e) => e.type === typeToAdd)) return;
    const wasEmpty = editions.length === 0;
    setEditions([...editions, { type: typeToAdd, count: '', total: '' }]);
    setPendingType('');
    setFreeTextValue('');
    if (wasEmpty && onFirstAdd) {
      onFirstAdd();
    }
  };

  const handleSelectChange = (value: string) => {
    setPendingType(value);
    if (value !== FREE_TEXT_SENTINEL) {
      setFreeTextValue('');
    }
  };

  const exitFreeTextMode = () => {
    setPendingType('');
    setFreeTextValue('');
  };

  return (
    <>
      {editions.map((e, i) => (
        <div className="meas-row" key={i}>
          <div>
            {i === 0 && <FLabel>Edition Type</FLabel>}
            <div className="meas-fixed-type">{e.type}</div>
          </div>
          <div>
            {i === 0 && <FLabel htmlFor={`edition-total-${i}`}>Quantity</FLabel>}
            <Input
              id={`edition-total-${i}`}
              value={e.total}
              onChange={(ev) => update(i, { total: ev.target.value, count: ev.target.value })}
              placeholder="0"
              inputMode="numeric"
            />
          </div>
          <div>
            <button
              type="button"
              className="remove-btn"
              onClick={() => remove(i)}
              title="Remove edition"
              aria-label={`Remove edition ${i + 1}`}
            >
              <TrashIcon />
            </button>
          </div>
        </div>
      ))}

      {availableTypes.length > 0 && (
        <div className="meas-add-row">
          <TextField
            select
            size="small"
            value={pendingType}
            onChange={(e) => handleSelectChange(e.target.value)}
            sx={{ width: 220 }}
            slotProps={{
              inputLabel: { shrink: false },
              select: { inputProps: { 'aria-label': 'Edition type to add' } },
            }}
          >
            <MenuItem value="" disabled>
              Select edition type
            </MenuItem>
            {availableTypes.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          {isFreeTextMode && (
            <TextField
              size="small"
              value={freeTextValue}
              onChange={(e) => setFreeTextValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  exitFreeTextMode();
                }
              }}
              placeholder="Enter custom edition type"
              sx={{ width: 220 }}
              inputProps={{ 'aria-label': 'Custom Edition Text' }}
            />
          )}
          <Btn variant="outlined" onClick={addPending} disabled={isAddDisabled}>
            <PlusIcon />
            Add
          </Btn>
        </div>
      )}
    </>
  );
};

/**
 * Multiples / Editions section.
 *
 * The legacy "This work has multiple editions" checkbox is hidden — the
 * Add row is always visible. We preserve the underlying `hasMultiples`
 * boolean and flip it to true automatically when the user adds their
 * first edition row.
 */
export const MultiplesEditionsSection = ({ form }: Props) => {
  const { editions, setEditions, hasMultiples, setHasMultiples } = form;

  const editionOptions = en.wizard.describe.steps.workType.editions.options;

  const summary =
    editions.length === 0
      ? hasMultiples
        ? 'Multiples — none added yet'
        : undefined
      : editions.map((e) => `${e.type} ${e.total}`).join(', ');

  const handleSetEditions = (next: Edition[]) => {
    setEditions(next);
  };

  const handleFirstAdd = () => {
    if (!hasMultiples) {
      setHasMultiples(true);
    }
  };

  return (
    <Acc
      id="sec-multiples"
      title="Multiples / Editions"
      summary={summary}
    >
      <EditionRows
        editions={editions}
        setEditions={handleSetEditions}
        options={editionOptions}
        onFirstAdd={handleFirstAdd}
      />
    </Acc>
  );
};
